import React, { useRef } from 'react';
import { 
  FaSliders, 
  FaMagnifyingGlass, 
  FaFileImport,
  FaFileExport, 
  FaCamera, 
  FaDownload, 
  FaXmark 
} from "react-icons/fa6";

export default function EditorHelpModal({ isOpen, onClose }) {
  const contentRef = useRef(null);

  if (!isOpen) return null;

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="help-modal-overlay" onClick={onClose}>
      <style>{`
        .help-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          background-color: rgba(0, 0, 0, 0.8);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 99999;
          backdrop-filter: blur(4px);
        }
        .help-modal-container {
          background-color: #120e0a;
          border: 2px solid #4a3c31;
          width: 90%;
          max-width: 1000px;
          height: 80vh;
          border-radius: 8px;
          display: flex;
          overflow: hidden;
          box-shadow: 0 10px 30px rgba(0,0,0,0.8);
          text-align: left;
          position: relative; /* Keeps close button relative to this container */
        }
        /* Sidebar Navigation */
        .help-sidebar {
          width: 260px;
          background-color: #1c1510;
          border-right: 2px solid #4a3c31;
          padding: 2rem 1.5rem;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          user-select: none;
          text-align: left;
        }
        .help-sidebar-header {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 1.5rem;
          border-bottom: 1px solid #4a3c31;
          padding-bottom: 0.5rem;
        }
        .help-sidebar-logo {
          height: 28px;
          width: auto;
          object-fit: contain;
        }
        .help-sidebar h1 {
          font-size: 1.4rem;
          color: #ffb01f;
          margin: 0;
          font-weight: 600;
          font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, sans-serif;
        }
        .help-sidebar ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        .help-sidebar li {
          margin-bottom: 0.8rem;
        }
        .help-sidebar button {
          background: none;
          border: none;
          color: #a19580;
          text-align: left;
          width: 100%;
          font-size: 0.95rem;
          cursor: pointer;
          padding: 0.25rem 0;
          transition: color 0.15s ease;
          font-family: inherit;
        }
        .help-sidebar button:hover {
          color: #ffb01f;
        }
        /* Fixed Close Button */
        .help-close-btn {
          position: absolute;
          top: 20px;
          right: 20px;
          background: none;
          border: none;
          color: #a19580;
          font-size: 1.5rem;
          cursor: pointer;
          transition: color 0.15s ease;
          z-index: 100000; /* Stays above content and does not scroll */
        }
        .help-close-btn:hover {
          color: #ffb01f;
        }
        /* Content Area */
        .help-content-pane {
          flex: 1;
          padding: 3rem;
          overflow-y: auto;
          color: #e0d3b8;
          font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, sans-serif;
          text-align: left;
        }
        .help-content-pane h2 {
          font-size: 2.2rem;
          color: #ffb01f;
          margin-bottom: 0.5rem;
          text-align: left;
        }
        .help-content-pane h3 {
          font-size: 1.5rem;
          color: #ffb01f;
          margin-bottom: 1rem;
          border-bottom: 1px solid #4a3c31;
          padding-bottom: 0.3rem;
          text-align: left;
        }
        .help-content-pane p {
          color: #e0d3b8;
          margin-bottom: 1rem;
          line-height: 1.6;
          text-align: left;
        }
        .help-content-pane ul, 
        .help-content-pane ol {
          margin-bottom: 1.5rem;
          padding-left: 1.5rem;
          text-align: left;
        }
        .help-content-pane li {
          margin-bottom: 0.5rem;
          text-align: left;
        }
        .help-content-pane strong {
          color: #fff;
        }
        .help-note {
          background-color: rgba(255, 176, 31, 0.05);
          border-left: 4px solid #ffb01f;
          padding: 1rem 1.25rem;
          margin: 1.5rem 0;
          border-radius: 4px;
          text-align: left;
        }
        .help-note p {
          margin: 0;
          text-align: left;
        }
        .help-kbd {
          background-color: #2d221a;
          border: 1px solid #4a3c31;
          border-radius: 4px;
          color: #fff;
          display: inline-block;
          font-size: 0.85em;
          font-weight: 600;
          padding: 0.1rem 0.4rem;
          white-space: nowrap;
          box-shadow: 0 1px 0 rgba(0,0,0,0.2);
        }
        .help-feature-list {
          display: grid;
          gap: 1.5rem;
          margin: 1.5rem 0;
          text-align: left;
        }
        .help-feature-item {
          background-color: rgba(255, 255, 255, 0.02);
          border: 1px solid #4a3c31;
          padding: 1.25rem;
          border-radius: 6px;
          text-align: left;
        }
        .help-feature-item h4 {
          color: #ffb01f;
          margin-bottom: 0.5rem;
          font-size: 1.1rem;
          text-align: left;
        }
        .help-icon {
          color: #ffb01f;
          vertical-align: middle;
          margin-right: 4px;
        }
      `}</style>

      <div className="help-modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Fixed Close Button */}
        <button className="help-close-btn" onClick={onClose} aria-label="Close modal">
          <FaXmark />
        </button>

        {/* Navigation Sidebar */}
        <aside className="help-sidebar">
          <div>
            <div className="help-sidebar-header">
              <img 
                src="/images/hearthcards-logo.webp" 
                alt="" 
                className="help-sidebar-logo"
                onError={(e) => e.currentTarget.style.display = 'none'}
              />
              <h1>Help</h1>
            </div>
            <ul>
              <li><button onClick={() => scrollToSection('canvas')}>1. Card Preview</button></li>
              <li><button onClick={() => scrollToSection('art-tuning')}>2. Art & Image Adjustments</button></li>
              <li><button onClick={() => scrollToSection('text-formatting')}>3. Text Sizing & Overrides</button></li>
              <li><button onClick={() => scrollToSection('drag-drop')}>4. Drag & Drop Loading</button></li>
              <li><button onClick={() => scrollToSection('custom-assets')}>5. Custom & Community Assets</button></li>
              <li><button onClick={() => scrollToSection('import-db')}>6. Database Card Importing</button></li>
              <li><button onClick={() => scrollToSection('file-ops')}>7. Saving & Exporting</button></li>
            </ul>
          </div>
        </aside>

        {/* Main Content Area */}
        <main className="help-content-pane" ref={contentRef}>
          <header style={{ marginBottom: '3rem', borderBottom: '1px solid #4a3c31', paddingBottom: '1.5rem', textAlign: 'left' }}>
            <h2>Editor Documentation</h2>
            <p style={{ color: '#a19580', fontSize: '1.1rem', textAlign: 'left' }}>
              A reference guide to the non-obvious features, hotkeys, and interactive systems of the HearthCards creation tool.
            </p>
          </header>

          {/* 1. Interactive Canvas */}
          <section id="canvas" style={{ marginBottom: '3.5rem', scrollMarginTop: '2rem' }}>
            <h3>1. Card Preview</h3>
            <p>The area on the right with the card:</p>
            
            <div className="help-feature-list">
              <div className="help-feature-item">
                <h4>Art Panning (Dragging)</h4>
                <p>Click and hold left mouse button anywhere within the visible artwork boundary, then drag to reposition the art. Panning art is dampened a little bit to allow precise placement. You can adjust how strong it is in the Settings.</p>
              </div>

              <div className="help-feature-item">
                <h4>Focus & Precision Controls</h4>
                <p>If you click on the art to focus it, you can fine-tune positioning using your keyboard:</p>
                <ul>
                  <li>Use <kbd className="help-kbd">Arrow Keys</kbd> to move the image a little bit.</li>
                  <li>Hold <kbd className="help-kbd">Shift</kbd> + <kbd className="help-kbd">Arrow Keys</kbd> to pan the image in bigger increments.</li>
                </ul>
              </div>

              <div className="help-feature-item">
                <h4>Mousewheel Zooming</h4>
                <p>Hover your cursor anywhere over the card canvas and use your mouse scroll wheel to zoom in or out.</p>
                <ul>
                  <li>Hold <kbd className="help-kbd">Shift</kbd> while scrolling to accelerate zoom speed.</li>
                </ul>
              </div>
            </div>
          </section>

          {/* 2. Art & Image Adjustments */}
          <section id="art-tuning" style={{ marginBottom: '3.5rem', scrollMarginTop: '2rem' }}>
            <h3>2. Art & Image Adjustments</h3>
            <p>You can fine-tune original artwork without leaving the browser using the adjustments panel located near the card text controls:</p>
            
            <ul>
              <li>
                <strong>Art Adjustments:</strong> Click the Sliders icon (<FaSliders className="help-icon" />) next to the font size indicators to display the overlay.
              </li>
              <li><strong>Brightness & Contrast:</strong></li>
              <li><strong>Saturation & Hue Shift:</strong></li>
              <li><strong>Flip Horizontal:</strong> Flip the artwork horizontaly.</li>
              <li><strong>Reset:</strong> Clear your adjustment layers back to default values by clicking "Reset" at the top of the dropdown.</li>
            </ul>
          </section>

          {/* 3. Text Sizing & Overrides */}
          <section id="text-formatting" style={{ marginBottom: '3.5rem', scrollMarginTop: '2rem' }}>
            <h3>3. Text Sizing & Overrides</h3>
            <p>The editor automatically manages spacing, line breaks, bolding and font size reduction to ensure your text fits within standard boundaries.</p>

            <div className="help-feature-list">
              <div className="help-feature-item">
                <h4>Keyword Bolding</h4>
                <p>Keywords (such as <strong>Battlecry:</strong>, <strong>Discover</strong>, <strong>Taunt</strong> etc) are recognized by the formatting system and rendered in bold automatically. You do not need to manually add bold tags to these.</p>
              </div>

              <div className="help-feature-item">
                <h4>Manual Overrides & Resets</h4>
                <p>Use the <kbd className="help-kbd">+</kbd> and <kbd className="help-kbd">-</kbd> buttons on the side of the text panel to manually bump font size up or down. Once changed, the font size display turns red to signal a manual override is active. To return the card to the automatic maximum font size, click the red size value.</p>
              </div>

              <div className="help-feature-item">
                <h4>Bold & Italic Tags</h4>
                <p>Highlight text inside the input box and click either the <kbd className="help-kbd">B</kbd> or <kbd className="help-kbd">I</kbd> icons to wrap the selection with tags (<code>[b][/b]</code> and <code>[i][/i]</code>). If nothing is selected, clicking the buttons places the cursor right inside empty tags so you can start typing immediately.</p>
              </div>
            </div>
          </section>

          {/* 4. Drag & Drop Loading */}
          <section id="drag-drop" style={{ marginBottom: '3.5rem', scrollMarginTop: '2rem' }}>
            <h3>4. Drag & Drop Loading</h3>
            <p>The entire editor viewport functions as a drag-and-drop landing area. This feature behaves contextually depending on the file type dropped:</p>

            <ul>
              <li><strong>Drop an Image:</strong> Dragging any standard image file (PNG, JPG, WEBP) from your desktop directly onto the browser window loads it as art for the card, overriding your current image.</li>
              <li><strong>Drop a Card JSON State:</strong> Dragging an exported configuration file ending in <code>.json</code> or <code>.hcards</code> restores the full state of the card, including artwork.</li>
            </ul>
          </section>

          {/* 5. Custom & Community Assets */}
          <section id="custom-assets" style={{ marginBottom: '3.5rem', scrollMarginTop: '2rem' }}>
            <h3>5. Custom & Community Assets</h3>
            <p>Beyond standard card classes and emblems, you can access community assets via the <strong>Custom Assets</strong>:</p>

            <ul>
              <li>
                <strong>Accessing Custom Assets:</strong> Click "Custom" or click the magnifying glass icon (<FaMagnifyingGlass className="help-icon" />) next to the selector.
              </li>
              <li><strong>Managing Favorites:</strong> Clicking the Star icon next to public custom classes, emblems, or card frames adds them to your favorites list for quick access.</li>
              <li><strong>Asset Creation & Administration:</strong> Under the custom emblem interface, you can access the built-in <strong>+ Create</strong> tab to upload custom emblems. You can rename or permanently delete your uploads directly from the "My Assets" tab.</li>
            </ul>
          </section>

          {/* 6. Database Card Importing */}
          <section id="import-db" style={{ marginBottom: '3.5rem', scrollMarginTop: '2rem' }}>
            <h3>6. Database Card Importing</h3>
            <p>The Importer allows you to import complete cards or only art from existing cards:</p>

            <div className="help-feature-list">
              <div className="help-feature-item">
                <h4>Full Card vs. Art Only Import</h4>
                <p>When searching for a card in the database, the tool provides two options:</p>
                <ul>
                  <li><strong>Full:</strong> Rewrites all current parameters (mana cost, card type etc).</li>
                  <li><strong>Art:</strong> Leaves your current card parameters unchanged, but imports only the art.</li>
                </ul>
              </div>
            </div>
          </section>

          {/* 7. Saving & Exporting */}
          <section id="file-ops" style={{ marginBottom: '3.5rem', scrollMarginTop: '2rem' }}>
            <h3>7. Saving & Exporting</h3>
            <p>Save or transfer your creations using these quick-action buttons located under the emblem selection panel:</p>

            <div className="help-feature-list">
              <div className="help-feature-item">
                <h4>Import JSON (<FaFileImport className="help-icon" />)</h4>
                <p>Select a <code>.json</code> file from your computer to reload a card back into the editor with all its parameters and art.</p>
              </div>

              <div className="help-feature-item">
                <h4>Export JSON (<FaFileExport className="help-icon" />)</h4>
                <p>Downloads a <code>.json</code> text file capturing all input configurations, text formatting values, zoom levels, adjustments, and art placement coordinates. This ensures you can reload and edit the card later.</p>
              </div>

              <div className="help-feature-item">
                <h4>Copy Card to Clipboard (<FaCamera className="help-icon" />)</h4>
                <p>Renders your card at high resolution in the background and copies the finalized card to your clipboard. You can paste the card directly into applications like Discord, paint programs, or others without downloading a local file.</p>
              </div>

              <div className="help-feature-item">
                <h4>Download Preview (<FaDownload className="help-icon" />)</h4>
                <p>Renders current preview of your card as high resolution PNG and downloads it as an image file to your device downloads folder.</p>
              </div>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}